<script setup lang="ts">
import { onMounted } from 'vue';
import {
  DxDataGrid,
  DxColumn,
  DxMasterDetail,
  DxScrolling,
} from 'devextreme-vue/data-grid';
import { getAggregatorsLookup } from '@botto/core';
import type { ClickEvent } from '../api';
import { formatDateTime } from '../utils/format';
import { formatDeviceLabel } from '../utils/clickLogUi';
import { trackerGridProps } from '../utils/gridUi';
import CountryFlag from './CountryFlag.vue';
import DeviceIcon from './DeviceIcon.vue';

defineProps<{
  clicks: ClickEvent[];
  loading?: boolean;
  /** Show substitute link code (target detail). */
  showCode?: boolean;
}>();

const aggregatorsLookup = getAggregatorsLookup();

onMounted(() => {
  void aggregatorsLookup.ensureLoaded();
});

function formatTime(r: ClickEvent) {
  return formatDateTime(r.event_time);
}

function formatDevice(r: ClickEvent) {
  return formatDeviceLabel(r.device_type, r.os);
}

function formatAggregator(r: ClickEvent) {
  if (r.aggregator_id == null || r.aggregator_id === '') return '—';
  return aggregatorsLookup.labelOf(r.aggregator_id) || String(r.aggregator_id);
}
</script>

<template>
  <DxDataGrid
    class="click-log-grid"
    :data-source="clicks"
    v-bind="trackerGridProps"
    :loading="!!loading"
    :paging="{ enabled: false }"
    :word-wrap-enabled="false"
    key-expr="click_id"
    height="100%"
    width="100%"
  >
    <DxScrolling mode="standard" />
    <DxMasterDetail :enabled="true" template="clickDetail" />

    <DxColumn
      data-field="event_time"
      caption="Время"
      :min-width="170"
      :width="180"
      :calculate-cell-value="formatTime"
    />
    <DxColumn
      v-if="showCode"
      data-field="code"
      caption="Code"
      css-class="mono"
      :min-width="90"
      :width="110"
    />
    <DxColumn data-field="ip" caption="IP" css-class="mono" :min-width="160" :width="260" />
    <DxColumn caption="Страна" :min-width="100" :width="120" cell-template="countryCell" />
    <DxColumn data-field="language" caption="Язык" css-class="mono" :min-width="70" :width="90" />
    <DxColumn caption="Устройство" :min-width="200" :width="260" cell-template="deviceCell" />
    <DxColumn data-field="outcome" caption="Outcome" :min-width="100" :width="120" />
    <DxColumn
      data-field="edge_node"
      caption="Edge"
      css-class="mono"
      :min-width="100"
      :width="130"
      :calculate-cell-value="(r: ClickEvent) => r.edge_node || '—'"
    />
    <DxColumn
      caption="Агрегатор"
      :min-width="140"
      :width="200"
      :calculate-cell-value="formatAggregator"
    />

    <template #countryCell="{ data }">
      <CountryFlag :code="data.data.country" />
    </template>
    <template #deviceCell="{ data }">
      <span class="device-cell">
        <DeviceIcon :device-type="data.data.device_type" :is-bot="data.data.is_bot" />
        <span class="device-cell__text">{{ formatDevice(data.data) }}</span>
      </span>
    </template>

    <template #clickDetail="{ data }">
      <div class="details-grid detail-expand">
        <div class="muted">Bot</div>
        <div>{{ data.data.is_bot ? 'yes' : 'no' }}</div>
        <div class="muted">UA</div>
        <div class="mono">{{ data.data.user_agent || '—' }}</div>
        <div class="muted">Browser</div>
        <div>{{ data.data.browser || '—' }}</div>
        <div class="muted">City / ASN</div>
        <div>{{ data.data.city || '—' }} / {{ data.data.asn || '—' }} ({{ data.data.isp || '—' }})</div>
        <div class="muted">Referer</div>
        <div class="mono">{{ data.data.referer || '—' }}</div>
        <div class="muted">Query</div>
        <div class="mono">{{ data.data.query_string || '—' }}</div>
        <div class="muted">Bot reasons</div>
        <div>{{ data.data.bot_reasons || '—' }}</div>
        <div class="muted">Visitor</div>
        <div class="mono">{{ data.data.visitor_key || '—' }}</div>
        <div class="muted">Unique</div>
        <div>{{ data.data.is_unique_link ? 'yes' : 'no' }}</div>
        <div class="muted">Edge node</div>
        <div class="mono">{{ data.data.edge_node || '—' }}</div>
      </div>
    </template>
  </DxDataGrid>
</template>

<style scoped>
.device-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  max-width: 100%;
}

.device-cell__text {
  white-space: nowrap;
}

.details-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 6px 12px;
  font-size: 0.875rem;
}

.detail-expand {
  padding: 8px 12px;
}

.muted {
  color: var(--dx-color-text-secondary, #666);
}

:deep(.mono) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}
</style>
